import React, { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Home from "./Home";
import Listings from "./Listings";
import Featured from "./Featured";
import About from "./About";
import ContactUs from "./ContactUs";
import Footer from "./Footer";

import { getAllProperties } from "../utils/api";

function PublicLandingPage() {
  const homeRef = useRef(null);
  const listingsRef = useRef(null);
  const featuredRef = useRef(null);
  const aboutRef = useRef(null);
  const contactRef = useRef(null);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Banner state
  const [banner, setBanner] = useState(null);

  const location = useLocation();

  useEffect(() => {
    getAllProperties()
      .then((res) => {
        if (res?.success) {
          setProperties(res.properties || []);
        } else {
          setError(res?.message || "Failed to load properties.");
          setBanner({
            type: "danger",
            message: res?.message || "Failed to load properties.",
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching properties:", err);
        setError("Failed to load properties. Please try again later.");
        setBanner({ type: "danger", message: "Failed to load properties." });
      })
      .finally(() => setLoading(false));
  }, []);

  // ✅ Scroll to section if coming back from PropertyDetails
  useEffect(() => {
    if (location.state?.scrollTo) {
      const sectionMap = {
        listings: listingsRef,
        featured: featuredRef,
      };
      sectionMap[location.state.scrollTo]?.current?.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [location]);

  const scrollToSection = (ref) =>
    ref.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="public-landing">
      {/* Navbar wired to scroll handlers */}
      <Navbar
        onHome={() => scrollToSection(homeRef)}
        onListings={() => scrollToSection(listingsRef)}
        onFeatured={() => scrollToSection(featuredRef)}
        onAbout={() => scrollToSection(aboutRef)}
        onContact={() => scrollToSection(contactRef)}
      />

      {/* ✅ Banner display */}
      {banner && (
        <div
          className={`alert alert-${banner.type} text-center mb-0 rounded-0`}
          role="alert"
        >
          {banner.message}
        </div>
      )}

      {/* Sections */}
      <section ref={homeRef} className="pt-5 mt-5">
        <Home />
      </section>

      <section ref={listingsRef} className="container mt-5 pt-4">
        {loading ? (
          <p className="text-muted">Loading properties...</p>
        ) : error ? (
          <p className="text-danger">{error}</p>
        ) : (
          <Listings properties={properties} setBanner={setBanner} />
        )}
      </section>

      <section ref={featuredRef} className="container mt-5 pt-4">
        {loading ? (
          <p className="text-muted">Loading featured properties...</p>
        ) : error ? (
          <p className="text-danger">{error}</p>
        ) : (
          <Featured properties={properties} setBanner={setBanner} />
        )}
      </section>

      <section ref={aboutRef} className="container mt-5 pt-4">
        <About />
      </section>

      <section ref={contactRef} className="container mt-5 pt-4">
        <ContactUs setBanner={setBanner} />
      </section>

      <Footer />
    </div>
  );
}

export default PublicLandingPage;
